//@ts-check

class ClearanceCaptureService {
	constructor($http, $window, $timeout, $rootScope, InternalLinksService, LocalStorageService) {
		this.$http = $http;
		this.$window = $window;
		this.$timeout = $timeout;
		this.$rootScope = $rootScope;
		this.LocalStorageService = LocalStorageService;
		this.InternalLinksService = InternalLinksService;

		this.init();
	}

	init() {
		this.utilities = [];
		this.schemeInformation = null;
		this.purchaserData = {};
		this.isSubmitted = false;
		this.maritalStatuses = [];
		this.clearanceCapture = {};
		this.contactPersonData = {};
		this.bankNames = '';
	}

	async loadClearanceCaptureByCorrelationId(correlationId) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			if (!correlationId) {
				throw new Error('Correlation Id must be supplied.');
			}

			let url = await this.InternalLinksService.getLink('GetClearance');

			if (!url) {
				await this.InternalLinksService.getLinks('api/clearance/links', false);
				url = await this.InternalLinksService.getLink('GetClearance');
			}
			url = `${url}?correlationId=${correlationId}`;

			const results = await this.$http.get(url);
			const { data } = results;
			this.clearanceCapture = this._map(data);
			const { IsSubmitted } = data || {
				IsSubmitted: false
			};

			this.isSubmitted = IsSubmitted;
			this.$timeout(() => promiseHandler.resolve(data));
		} catch (err) {
			const msg = `Error while fetching clearance data`;
			console.error(msg, err);
			this.clearanceCapture = {};
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	async getPurchaser(purchaserId) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			if (!purchaserId) {
				throw new Error('No purchaserId.');
			}

			let url = await this.InternalLinksService.getLink('GetPurchaser');

			if (!url) {
				await this.InternalLinksService.getLinks('api/clearance/links', false);
				url = await this.InternalLinksService.getLink('GetPurchaser');
			}

			url = `${url}?purchaserId=${purchaserId}`;

			const results = await this.$http.get(url);
			let { data } = results;

			const dataToBeMapped = {
				Purchaser: [data]
			};

			data = this._mapRejectedPurchasersDocuments(dataToBeMapped).Purchaser[0];
			this._updatePurchaser(data);

			if (data.MaritalStatus == 0) {
				data.MaritalStatus = undefined;
			}

			this.purchaserData = data;

			this.$timeout(() => promiseHandler.resolve(data));
		} catch (err) {
			this.purchaserData = {};
			const msg = `Error while getting purchaser: ${err}`;
			console.error(msg, err);
			this.$timeout(() => promiseHandler.reject(msg));
		}

		return promise;
	}

	async getContactPerson(contactPersonId) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			if (!contactPersonId) {
				throw new Error('No contactPersonId.');
			}

			let url = await this.InternalLinksService.getLink('GetContactPerson');

			if (!url) {
				await this.InternalLinksService.getLinks('api/clearance/links', false);
				url = await this.InternalLinksService.getLink('GetContactPerson');
			}

			url = `${url}?contactPersonId=${contactPersonId}`;

			const results = await this.$http.get(url);
			let { data } = results;

			if (data) {
				const LegalEntity = {
					ContactPerson: [data]
				};

				data = this._mapRejectedContactPersonDocuments({
					LegalEntity
				}).LegalEntity.ContactPerson[0];

				this._updateContactPerson(data);

				if (data.MaritalStatus == 0) {
					data.MaritalStatus = undefined;
				}

				this.contactPersonData = data;
			}

			this.$timeout(() => promiseHandler.resolve(data));
		} catch (err) {
			this.contactPersonData = {};
			const msg = `Error while getting purchaser`;
			console.error(msg, err);
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	async getUtilityInfo() {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			const schemeReference = this.clearanceCapture.SchemeReference;
			if (!schemeReference) {
				throw 'no scheme reference supplied';
			}

			let url = await this.InternalLinksService.getLink('GetImportantBuildingInfo');
			if (!url) {
				await this.InternalLinksService.getLinks('api/clearance/links', false);
			}
			if (!url) {
				url = await this.InternalLinksService.getLink('GetImportantBuildingInfo');
			}
			const results = await this.$http.get(`${url}/${schemeReference}`);

			const { Data, WasSuccessful } = results.data;

			if (WasSuccessful) {
				this.utilities = Data;

				this.$timeout(() => promiseHandler.resolve(Data));
			} else {
				throw new Error(`Could not load Utility Information for Card Code: ${schemeReference}`);
			}
		} catch (err) {
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	async getSchemeInfo() {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});
			const schemeReference = this.clearanceCapture.SchemeReference;
			if (!schemeReference) {
				throw 'no scheme reference supplied';
			}

			let url = await this.InternalLinksService.getLink('GetSchemeInfo');
			if (!url) {
				await this.InternalLinksService.getLinks('api/clearance/links', false);
				url = await this.InternalLinksService.getLink('GetSchemeInfo');
			}
			const results = await this.$http.get(`${url}/${schemeReference}`);

			const { Data, WasSuccessful } = results.data;

			if (WasSuccessful) {
				this.schemeInformation = Data;

				this.$timeout(() => promiseHandler.resolve(Data));
			} else {
				throw new Error(`Error Loading Scheme Information for ${schemeReference}`);
			}
		} catch (err) {
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	async uploadPurchaseDocs(attachment, isLegalEntityDoc) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			if (!attachment) {
				throw new Error('no attachment');
			}

			let url = isLegalEntityDoc
				? await this.InternalLinksService.getLink('AddPurchaseAttachment')
				: await this.InternalLinksService.getLink('SaveClearanceHeaderDoc');

			if (!url) {
				await this.InternalLinksService.getLinks('api/clearance/links', false);
				url = isLegalEntityDoc
					? await this.InternalLinksService.getLink('AddPurchaseAttachment')
					: await this.InternalLinksService.getLink('SaveClearanceHeaderDoc');
			}

			const results = await this.$http.post(url, attachment);

			this.$timeout(() => promiseHandler.resolve(results.data));
		} catch (err) {
			const msg = `Error while Adding Attachment`;
			console.error(msg, err);
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	async saveClearanceHeader(payload) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});
			if (!payload) {
				throw new Error('no attachment');
			}

			let url = await this.InternalLinksService.getLink('SaveClearanceHeader');
			if (!url) {
				await this.InternalLinksService.getLinks('api/clearance/links', false);
				url = await this.InternalLinksService.getLink('SaveClearanceHeader');
			}

			const results = await this.$http.post(url, payload);
			const { data } = results;
			this.$timeout(() => promiseHandler.resolve(data));
		} catch (error) {
			const msg = `Error while saving Clearance Header.`;
			console.error(msg, error);
			this.$timeout(() => promiseHandler.reject(error));
		}

		return promise;
	}

	async saveClearanceRegHeaderAttachment(dateOfRegistration, attachment) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});
			if (!attachment) {
				throw new Error('no attachment');
			}
			if (!dateOfRegistration) {
				throw new Error('no Date of Registration');
			}

			let url = await this.InternalLinksService.getLink('SubmitRegistrationClearanceHeaderAttachment');
			if (!url) {
				await this.InternalLinksService.getLinks('api/clearance/links', false);
				url = await this.InternalLinksService.getLink('SubmitRegistrationClearanceHeaderAttachment');
			}

			const results = await this.$http.post(url, { dateOfRegistration, attachment });
			const { data } = results;
			this.$timeout(() => promiseHandler.resolve(data));
		} catch (error) {
			const msg = `Error while saving Registration Clearance Header.`;
			console.error(msg, error);
			this.$timeout(() => promiseHandler.reject(error));
		}

		return promise;
	}

	async saveLegalEntity(payload) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			if (!payload) {
				throw new Error('no attachment');
			}

			let url = this.InternalLinksService.getLink('SaveLegalEntity');

			if (!url) {
				await this.InternalLinksService.getLinks('api/clearance/links', false);
				url = await this.InternalLinksService.getLink('SaveLegalEntity');
			}

			url = url.replace('{clearanceHeaderId}', payload.ClearanceHeaderId);

			const results = await this.$http.post(url, payload);
			this.$timeout(() => promiseHandler.resolve(results.data));
		} catch (err) {
			const msg = `Error while Adding Attachment`;
			console.error(msg, err);
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	async upsertContactPerson(contactPerson) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});
			if (!contactPerson) {
				throw new Error('Contact person must be supplied.');
			}
			let url = await this.InternalLinksService.getLink('UpsertContactPerson');

			if (!url) {
				await this.InternalLinksService.getLinks('api/clearance/links', false);
				url = await this.InternalLinksService.getLink('UpsertContactPerson');
			}

			const results = await this.$http.post(url, contactPerson);
			let { data } = results;

			if (data) {
				const LegalEntity = {
					ContactPerson: [data]
				};

				data = this._mapRejectedContactPersonDocuments({
					LegalEntity
				}).LegalEntity.ContactPerson[0];

				this._updateContactPerson(data);
				this.contactPersonData = data;
			}

			this.$timeout(() => promiseHandler.resolve(results.data));
		} catch (err) {
			const msg = `Error while saving contact person`;
			console.error(msg, err);
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	async upsertPurchaser(purchaser) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			if (!purchaser) {
				throw new Error('Purchaser must be supplied.');
			}

			let url = await this.InternalLinksService.getLink('UpsertPurchaser');

			if (!url) {
				await this.InternalLinksService.getLinks('api/clearance/links', false);
				url = await this.InternalLinksService.getLink('UpsertPurchaser');
			}

			const results = await this.$http.post(url, purchaser);
			let { data } = results;

			const dataToBeMapped = {
				Purchaser: [data]
			};

			data = this._mapRejectedPurchasersDocuments(dataToBeMapped).Purchaser[0];

			this._updatePurchaser(data);
			this.purchaserData = data;

			this.$timeout(() => promiseHandler.resolve(results.data));
		} catch (err) {
			const msg = `Error while saving purchaser`;
			console.error(msg, err);
			this.$timeout(() => promiseHandler.reject(err));
		}
		return promise;
	}

	async deletePurchaser(purchaserId) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			if (!purchaserId) {
				throw new Error('no purchaser id.');
			}

			let url = await this.InternalLinksService.getLink('DeletePurchaser');

			if (!url) {
				await this.InternalLinksService.getLinks('api/clearance/links', false);
				url = await this.InternalLinksService.getLink('DeletePurchaser');
			}

			url += `?purchaserId=${purchaserId}`;

			const results = await this.$http.post(url);
			const { data } = results;

			if (data) {
				this._mapDeletedPurchaser(purchaserId);
			}

			this.$timeout(() => promiseHandler.resolve(results.data));
		} catch (err) {
			const msg = `Error while deleting Purchaser`;
			console.error(msg, err);
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	async deleteContactPerson(contactPersonId) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			if (!contactPersonId) {
				throw new Error('no Contact Person id.');
			}

			let url = await this.InternalLinksService.getLink('DeleteContactPerson');
			if (!url) {
				await this.InternalLinksService.getLinks('api/clearance/links', false);
				url = await this.InternalLinksService.getLink('DeleteContactPerson');
			}

			url += `?contactPersonId=${contactPersonId}`;
			const results = await this.$http.post(url);
			const { data } = results;

			if (data) {
				this._mapDeletedContactPerson(contactPersonId);
			}

			this.$timeout(() => promiseHandler.resolve(results.data));
		} catch (err) {
			const msg = `Error while deleting Contact Person`;
			console.error(msg, err);
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	async addAttachment(attachment, docType, isLegalEntity) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			if (!attachment) {
				throw new Error('no attachment');
			}

			let url = '';

			if (docType == 1 || docType == 2) {
				if (isLegalEntity) {
					url = await this.InternalLinksService.getLink('AddContactPersonAttachment');
				} else {
					url = await this.InternalLinksService.getLink('AddPurchaserAttachment');
				}
			} else if (docType == 4) {
				url = await this.InternalLinksService.getLink('AddLegalEntityAttachment');
			} else if (docType == 3 || docType == 5 || docType == 6 || docType == 8) {
				url = await this.InternalLinksService.getLink('AddClearanceHeaderAttachment');
			}

			if (!url) {
				await this.InternalLinksService.getLinks('api/clearance/links', false);
				if (docType == 1 || docType == 2 || docType == 3) {
					if (isLegalEntity) {
						url = await this.InternalLinksService.getLink('AddContactPersonAttachment');
					} else {
						url = await this.InternalLinksService.getLink('AddPurchaserAttachment');
					}
				} else if (docType == 4) {
					url = await this.InternalLinksService.getLink('AddLegalEntityAttachment');
				} else if (docType == 5 || docType == 8) {
					url = await this.InternalLinksService.getLink('AddClearanceHeaderAttachment');
				}
			}

			const results = await this.$http.post(url, attachment);
			this.$timeout(() => promiseHandler.resolve(results.data));
		} catch (err) {
			const msg = `Error while Adding Attachment`;
			console.error(msg, err);
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	async deleteAttachment(attachment, isLegalEntity) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			if (!attachment) {
				throw new Error('No attachment.');
			}

			let url = await this.InternalLinksService.getLink('DeleteAttachment');

			if (!url) {
				await this.InternalLinksService.getLinks('api/clearance/links', false);
				url = await this.InternalLinksService.getLink('DeleteAttachment');
			}

			url = `${url}?attachmentId=${attachment.Id}&isLegalEntity=${isLegalEntity}&clearanceDocumentType=${attachment.ClearanceDocumentType}`;

			const results = await this.$http.post(url);
			this.$timeout(() => promiseHandler.resolve(results.data));
		} catch (err) {
			const msg = `Error while deleting attachment`;
			console.error(msg, err);
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	async submitClearanceCapture(clearanceHeaderId) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			if (!clearanceHeaderId) {
				throw new Error('No clearance Header Id.');
			}

			let url = await this.InternalLinksService.getLink('FinalizeClearances');

			if (!url) {
				await this.InternalLinksService.getLinks('api/clearance/links', false);
				url = await this.InternalLinksService.getLink('FinalizeClearances');
			}

			url = `${url}?clearanceHeaderId=${clearanceHeaderId}`;

			const results = await this.$http.post(url);
			this.$timeout(() => promiseHandler.resolve(results.data));
		} catch (err) {
			const msg = `Error while submitting clearance header`;
			console.error(msg, err);
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	async getConductRulesOrMemoForScheme(schemeReference) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			let url = this.InternalLinksService.getLink('GetConductRulesOrMemoForScheme');
			if (!url) {
				await this.InternalLinksService.getLinks('/api/documents/links');
				url = await this.InternalLinksService.getLink('GetConductRulesOrMemoForScheme');
			}

			url += `?schemeReference=${schemeReference}`;
			this.openNewTabReport(url, `Rules - ${schemeReference}`);
			this.$timeout(() => promiseHandler.resolve());
		} catch (err) {
			this.$timeout(() => promiseHandler.reject(`Couldn't load API 'links'`, err));
		}
		return promise;
	}

	async getMaritalStatuses() {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			let url = await this.InternalLinksService.getLink('GetMaritalStatuses');
			if (!url) {
				await this.InternalLinksService.getLinks('/api/clearance/links');
				url = await this.InternalLinksService.getLink('GetMaritalStatuses');
			}

			const results = await this.$http.get(url);
			this.maritalStatuses = results.data.filter(
				(maritalStatus) => maritalStatus.status.toLowerCase() !== 'unknown'
			);

			this.$timeout(() => promiseHandler.resolve(results.data));
		} catch (err) {
			const msg = `Error while fetching Marital Statuses`;
			console.error(msg, err);
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}
	
	async getBankingInstitutionName() {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			let url = await this.InternalLinksService.getLink('GetBankingInstitutionName');
			if (!url) {
				await this.InternalLinksService.getLinks('/api/clearance/links');
				url = await this.InternalLinksService.getLink('GetBankingInstitutionName');
			}
			const results = await this.$http.get(url);
			this.bankNames = results.data;
			this.$timeout(() => promiseHandler.resolve(results.data));
		} catch (err) {
			const msg = `Error while fetching Bank Names`;
			console.error(msg, err);
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}
	
	openNewTabReport(url, title) {
		const newTab = this.$window.open(url, '_blank');

		newTab.document.write(`
                <title>${title}</title>
                <style>
                    html:first-child>body {
                        margin: 0;
                        overflow: hidden;
                    }
                    iframe {
                        width: 100%;
                        height: 100%;
                    }
                </style>
                <iframe src="${url}">
                </iframe>
            `);
	}

	_map(data) {
		if (!data) return data;

		if (!data.IsSubmitted) {
			data = this._formatAndMapDate(data);
		} else {
			data.ValidDate = data.ValidDate.substr(0, 10);
		}

		data = this._mapSellerInfo(data);
		data = this._mapRejectedDocuments(data);

		return data;
	}

	_formatAndMapDate(original) {
		const mapped = original;
		const { ValidDate } = mapped;

		let momentDate,
			// @ts-ignore
			initDate = new moment(new Date()),
			// @ts-ignore
			momentInitDate = moment(ValidDate);

		if (ValidDate && momentInitDate.year() !== 1900) {
			// @ts-ignore
			momentDate = moment(ValidDate);
			if (initDate.isBefore(momentDate)) {
				initDate = momentDate;
			}
		} else {
			// @ts-ignore
			const currentMomentDate = moment(initDate);
			const currentDay = currentMomentDate.date();

			if (currentDay < 15) {
				initDate.add(2, 'M');
			} else {
				initDate.add(3, 'M');
			}

			// @ts-ignore
			initDate = moment(`${initDate.format('YYYY/MM/')}${initDate.daysInMonth()}`, 'YYYY/MM/DD');
		}

		mapped.ValidDate = initDate.format('YYYY/MM/DD');

		return mapped;
	}

	_mapRejectedDocuments(data) {
		data = this._mapRejectedHeaderDocuments(data);
		data = this._mapRejectedPurchasersDocuments(data);

		return data;
	}

	_mapRejectedHeaderDocuments(data) {
		const ClearanceHeaderRejectedAttachments = data.ClearanceHeaderAttachment
			? data.ClearanceHeaderAttachment.filter((attachment) => {
				return attachment.IsRejected;
			})
			: [];

		const { IsLegalEntity } = data;
		let LegalEntityRejectedAttachments;
		if (IsLegalEntity) {
			LegalEntityRejectedAttachments = data.LegalEntity.LegalEntityAttachment
				? data.LegalEntity.LegalEntityAttachment.filter((att) => {
					return att.IsRejected;
				})
				: [];
		}

		data = {
			...data,
			ClearanceHeaderRejectedAttachments,
			LegalEntityRejectedAttachments
		};

		if (ClearanceHeaderRejectedAttachments) {
			ClearanceHeaderRejectedAttachments.map((attachment) => {
				data.ClearanceHeaderAttachment = [
					...data.ClearanceHeaderAttachment.filter((attForRemoval) => attForRemoval.Id !== attachment.Id)
				];
			});
		}

		if (LegalEntityRejectedAttachments) {
			LegalEntityRejectedAttachments.map((att) => {
				data.LegalEntity.LegalEntityAttachment = [
					...data.LegalEntity.LegalEntityAttachment.filter((attForRemoval) => attForRemoval.Id !== att.Id)
				];
			});
		}

		return data;
	}

	_mapRejectedContactPersonDocuments(data) {
		let { LegalEntity } = data;
		let { ContactPerson } = LegalEntity;

		let mappedContactPerson;
		mappedContactPerson = ContactPerson.map((contactPerson) => {
			let RejectedAttachments;

			RejectedAttachments = contactPerson
				? contactPerson.ContactPersonAttachment
					? contactPerson.ContactPersonAttachment.filter((attachment) => {
						return attachment.IsRejected;
					})
					: []
				: null;

			contactPerson = {
				...contactPerson,
				RejectedAttachments
			};

			return contactPerson;
		});

		mappedContactPerson.forEach((mapped) => (mapped.MaritalStatus += ''));

		LegalEntity = {
			...LegalEntity,
			ContactPerson: mappedContactPerson
		};

		LegalEntity.ContactPerson = LegalEntity.ContactPerson.map((contactPerson) => {
			if (contactPerson.RejectedAttachments) {
				contactPerson.RejectedAttachments.map((attachment) => {
					let mappedAttachments;
					mappedAttachments = contactPerson.ContactPersonAttachment.filter(
						(attForRemoval) => attForRemoval.Id !== attachment.Id
					);
					contactPerson.ContactPersonAttachment = [...mappedAttachments];
				});
			}

			return contactPerson;
		});

		data = {
			...data,
			LegalEntity
		};

		return data;
	}

	_mapRejectedPurchasersDocuments(data) {
		const { Purchaser } = data;
		let mappedPurchasers;
		if (Purchaser) {
			mappedPurchasers = Purchaser.map((purchaser) => {
				let RejectedAttachments;
				RejectedAttachments = purchaser
					? purchaser.PurchaserAttachment
						? purchaser.PurchaserAttachment.filter((attachment) => {
							return attachment.IsRejected;
						})
						: []
					: null;

				purchaser = {
					...purchaser,
					RejectedAttachments
				};

				return purchaser;
			});

			mappedPurchasers.forEach((mapped) => (mapped.MaritalStatus += ''));

			data = {
				...data,
				Purchaser: mappedPurchasers
			};

			data.Purchaser = data.Purchaser.map((purchaser) => {
				if (purchaser.RejectedAttachments) {
					purchaser.RejectedAttachments.map((attachment) => {
						let mappedAttachments;
						mappedAttachments = purchaser.PurchaserAttachment.filter(
							(attForRemoval) => attForRemoval.Id !== attachment.Id
						);
						purchaser.PurchaserAttachment = [...mappedAttachments];
					});
				}

				return purchaser;
			});
		}

		return data;
	}

	_mapSellerInfo(data) {
		const oldSellerEmails = data.SellerEmail;
		data.SellerEmail = '';
		data.SellerEmail = this._mapSenderEmails(oldSellerEmails);

		return data;
	}

	_mapSenderEmails(sellerEmail) {
		const sellerEmails = sellerEmail.indexOf(';') ? sellerEmail.split(';') : [sellerEmail];

		let mappedSellerEmails = '';
		if (sellerEmails.length > 1) {
			for (const email of sellerEmails) {
				mappedSellerEmails += `${email}\n`;
			}
			mappedSellerEmails = mappedSellerEmails.substr(0, mappedSellerEmails.lastIndexOf('\n'));
		} else {
			mappedSellerEmails = sellerEmails[0];
		}

		return mappedSellerEmails;
	}

	_updatePurchaser(updatedPurchaser) {
		let isNew = true;
		const oldPurchaser = this.clearanceCapture.Purchaser.filter((obj) => obj.Id === updatedPurchaser.Id);

		if (oldPurchaser && oldPurchaser.length > 0) {
			isNew = false;
		}

		if (!isNew) {
			this.clearanceCapture.Purchaser.forEach((purchaser, i, array) => {
				if (purchaser.Id === updatedPurchaser.Id) {
					array[i] = updatedPurchaser;
				}
			});
		} else {
			this.clearanceCapture.Purchaser.push(updatedPurchaser);
		}
	}

	_updateContactPerson(updatedPerson) {
		let isNew = true;

		const oldContactPerson = this.clearanceCapture.LegalEntity.ContactPerson.filter(
			(obj) => obj.Id === updatedPerson.Id
		);
		if (oldContactPerson && oldContactPerson.length > 0) {
			isNew = false;
		}

		if (!isNew) {
			this.clearanceCapture.LegalEntity.ContactPerson.forEach((person, i, array) => {
				if (person.Id === updatedPerson.Id) {
					array[i] = updatedPerson;
				}
			});
		} else {
			this.clearanceCapture.LegalEntity.ContactPerson.push(updatedPerson);
		}
	}

	_mapDeletedContactPerson(id) {
		const deletedContactPerson = this.clearanceCapture.LegalEntity.ContactPerson.find(
			(contactPerson) => contactPerson.Id === id
		);
		if (deletedContactPerson) {
			this.clearanceCapture.LegalEntity.ContactPerson = this.clearanceCapture.LegalEntity.ContactPerson.filter(
				(contactPerson) => contactPerson.Id !== id
			);
		}
	}

	_mapDeletedPurchaser(id) {
		const deletedPurchaser = this.clearanceCapture.Purchaser.find((purchaser) => purchaser.Id === id);
		if (deletedPurchaser) {
			this.clearanceCapture.Purchaser = this.clearanceCapture.Purchaser.filter(
				(purchaser) => purchaser.Id !== id
			);
		}
	}
}

export default /* @ngInject */ ($http, $window, $timeout, $rootScope, InternalLinksService, LocalStorageService) =>
	new ClearanceCaptureService($http, $window, $timeout, $rootScope, InternalLinksService, LocalStorageService);
